import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Template from './template';
import { ProfileRegister } from 'components';
import { PartnerAuthState } from 'stores/PartnerAuthStore';
import { ProfileRegisterAction, ProfileRegisterState } from './store';
import { useApiRequest } from 'hooks';
import { apiUserProfileRegister, apiGetUserProfile } from 'api/ZimssaAPI';
import ZimssaConstant from 'commons/ZimssaConstant';
import {
  getYear,
  validateInput,
  validationEmailDomain,
} from 'commons/ZimssaHelper';
import {
  PAGE_SERVICE_SMALLMOVE_PROFILE_REGISTER_TITLE,
  PAGE_SERVICE_FAMILYMOVE_PROFILE_REGISTER_TITLE,
  PAGE_SERVICE_MOVECLEANING_PROFILE_REGISTER_TITLE,
  PLACEHOLDER_EMAIL_1,
  PLACEHOLDER_EMAIL_2,
  SELECT_DEFAULT_CARRIERYEAR,
  BUTTON_COMPLETE,
} from 'commons/ZimssaString';
import { TGuideType } from 'components/molecules/Popup/ImageGuide';
import { showModal } from 'commons/utils/showModal';

const DIRECT_INPUT_TEXT = '직접입력';

function ProfileRegisterView(): React.ReactElement {
  const history = useHistory();
  const { serviceType, serviceIdx } = useParams<{
    serviceType?: 'smallmove' | 'familymove' | 'move_cleaning';
    serviceIdx?: string;
  }>();
  const [profileResult, fetchProfile] = useApiRequest<any>(apiGetUserProfile);

  const [email1, setEmail1] = useState<string>('');
  const [email2, setEmail2] = useState<string>(DIRECT_INPUT_TEXT);
  const [email3, setEmail3] = useState<string>('');
  const profileDispatcher = useContext(ProfileRegisterAction);

  const { name, partnerIdx, partnerUserIdx } = useContext(PartnerAuthState);
  const { profileImage, carrierYear } = useContext(ProfileRegisterState);

  useEffect(() => {
    if (partnerUserIdx && serviceType && serviceIdx) {
      fetchProfile({
        type: 'REQUEST',
        body: [partnerUserIdx, serviceType, serviceIdx, Number(serviceIdx)],
      });
    }
  }, [fetchProfile, partnerUserIdx, serviceIdx, serviceType]);

  useEffect(() => {
    const { result, data } = profileResult;
    if (result === 'success' && data) {
      profileDispatcher({
        type: 'SET_STATE',
        value: {
          profileImage: data.profileImage,
          email: data.email,
          carrierYear: data.carrierYear,
        },
      });
      if (data.email) {
        setEmail1(data.email.split('@')[0]);
        if (
          ZimssaConstant.EMAIL_LIST.some(
            (item) => item.value === data.email.split('@')[1]
          )
        ) {
          setEmail2(data.email.split('@')[1]);
        } else {
          setEmail3(data.email.split('@')[1]);
        }
      }
    }
  }, [profileDispatcher, profileResult]);

  const profileRegisterProps = {
    profileImage: {
      type: 'PROFILE' as TGuideType,
      value: profileImage,
      name: name,
      isUpload: true,
      inputName: 'profileImage',
      imgDir: ZimssaConstant.IMG_DIR.PARTNER_USER.PROFILE.replace(
        '{{partnerIdx}}',
        `${partnerIdx}`
      ),
      onChangeHandler: useCallback(
        (type: any, value: string) => {
          profileDispatcher({
            type: 'profileImage',
            value: value,
          });
        },
        [profileDispatcher]
      ),
    },
    emailProps: {
      isDirectEmailEntry: email2 === DIRECT_INPUT_TEXT || email2 === '',
      email1: {
        inputName: 'email1',
        placeholder: PLACEHOLDER_EMAIL_1,
        valid: false,
        value: email1,
        onChange: useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;
          setEmail1(value);
        }, []),
      },
      email2: {
        inputName: 'email2',
        value: email2,
        children: ZimssaConstant.EMAIL_LIST,
        onChangeHandler: useCallback((type: any, value: string) => {
          if (value !== DIRECT_INPUT_TEXT) {
            setEmail3('');
          }
          setEmail2(value);
        }, []),
      },
      email3: {
        inputName: 'email3',
        placeholder: PLACEHOLDER_EMAIL_2,
        value: email3,
        isValid: true,
        onChange: useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;
          setEmail3(value);
        }, []),
      },
    },
    carrierYear: {
      inputName: 'carrierYear',
      value: carrierYear ? carrierYear : 0,
      children: [
        { name: SELECT_DEFAULT_CARRIERYEAR, value: 0 },
        ...getYear(100),
      ],
      onChangeHandler: useCallback(
        (type: any, value: string) => {
          profileDispatcher({
            type: 'carrierYear',
            value: parseInt(value, 10) || 0,
          });
        },
        [profileDispatcher]
      ),
    },
    btnNext: {
      contents: BUTTON_COMPLETE,
      isActive: (() => {
        if (
          validateInput(email1 ? email1.trim() : '') &&
          (email2 !== DIRECT_INPUT_TEXT || validationEmailDomain(email3)) &&
          validateInput(carrierYear)
        ) {
          if (serviceType === 'familymove') {
            return true;
          } else {
            return validateInput(profileImage ? profileImage.trim() : '');
          }
        } else {
          return false;
        }
      })(),
      onClick: useCallback(async () => {
        const result = await showModal({
          title: '프로필 정보를 등록 하시겠습니까?',
        });

        if (result) {
          apiUserProfileRegister({
            serviceIdx: serviceIdx ? Number(serviceIdx) : 0,
            serviceType: serviceType ? serviceType : '',
            user: {
              profileImage,
              email: `${email1.trim()}@${
                email3 === '' ? email2 : email3.trim()
              }`,
            },
            serviceUserInfo: {
              carrierYear,
            },
          }).then((response) => {
            const { result, message } = response;
            if (result === 'success') {
              alert('프로필을 등록 하였습니다.');
              history.goBack();
            } else {
              if (message) alert(message);
            }
          });
        }
      }, [
        carrierYear,
        email1,
        email2,
        email3,
        history,
        profileImage,
        serviceIdx,
        serviceType,
      ]),
    },
  };

  return (
    <Template
      title={(() => {
        if (serviceType === 'smallmove') {
          return PAGE_SERVICE_SMALLMOVE_PROFILE_REGISTER_TITLE;
        }

        if (serviceType === 'familymove') {
          return PAGE_SERVICE_FAMILYMOVE_PROFILE_REGISTER_TITLE;
        }

        if (serviceType === 'move_cleaning') {
          return PAGE_SERVICE_MOVECLEANING_PROFILE_REGISTER_TITLE;
        }

        return '예외';
      })()}
    >
      <ProfileRegister {...profileRegisterProps} />
    </Template>
  );
}

export default ProfileRegisterView;
